import { useTranslation } from 'react-i18next';

import { useSiteMetadataQuery } from '../../hooks/useSiteMetadataQuery';
import { useHelpCenterCTAData } from '../../hooks/useHelpCenterCTAData';
import type { IGlobalLayout } from '../../types';

import { useUniversalNavigationQuery } from '@klaviyo-mkt/gatsby-source-universal-navigation/src/hooks/useUniversalNavigationQuery';
import type { IUniversalMenuData } from '@klaviyo-mkt/gatsby-source-universal-navigation/src/types';

import { Breadcrumbs } from '@klaviyo-mkt/components/molecules/breadcrumbs';
import { Header } from '@klaviyo-mkt/components/organisms/header';
import { SubNav } from '@klaviyo-mkt/components/organisms/subnav';
import type { ISubNavItem } from '@klaviyo-mkt/components/organisms/subnav/types';
import KlaviyoStatusIndicator from '@klaviyo-mkt/components/organisms/klaviyo-status-indicator';
import { UniversalFooter } from '@klaviyo-mkt/components/organisms/universal-footer';
import { useAuthentication } from '@klaviyo-mkt/components/context/authentication';
import { getColorScheme } from '@klaviyo-mkt/components/shared/utils/functions';

import { useAlertBarQuery } from '../../hooks/useAlertBarQuery';
import { useBreadcrumbs, useShouldDisplayBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { useSiteLanguagesQuery } from '../../hooks/useSiteLanguagesQuery';
import SiteNavigationElementSchema from './site-navigation-element-schema';
import { transformMenuItems } from './utils';
import { useEffect } from 'react';
import { withOptimizely, type WithOptimizelyProps } from '@optimizely/react-sdk';

const Layout = ({
  breadcrumbsBackground,
  bodyClass = '',
  children,
  heroBackground,
  navigationColor,
  navigationStyle = 'default',
  path = '',
  optimizely,
  scrollIndicator,
  shortTitle = '',
  showBreadcrumbs = true,
  showFooter = true,
  showHeader = true,
  subnavMenu,
  translations,
}: IGlobalLayout & WithOptimizelyProps) => {

  const { authenticationState: { loggedIn } } = useAuthentication();

  const alertBarData = useAlertBarQuery();
  const siteMetadata = useSiteMetadataQuery();
  /* @ts-ignore: this is valid, I can't narrow the return type */
  const { copyright, ctas, ctasLoggedIn, footer, header, footerTesting, headerTesting, b2cHeader, helpCenter: helpCenterHeaderData, utility }: IUniversalMenuData = useUniversalNavigationQuery({ multiLocale: false }) || {};

  /* get the help center header data if we need it */
  const helpCenterCTAData = useHelpCenterCTAData();
  const { sites } = useSiteLanguagesQuery();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const crumbs = useBreadcrumbs(path, shortTitle, locale);
  const breadcrumbColor = heroBackground ? getColorScheme(heroBackground) : getColorScheme(breadcrumbsBackground);
  const { shouldShowBreadcrumbs } = useShouldDisplayBreadcrumbs(path, navigationStyle, showBreadcrumbs, locale);
  let headerToUse = path.includes('grow2') ? b2cHeader : header;
  let footerToUse = footer;
  if (navigationStyle === 'testing') {
    headerToUse = headerTesting;
    footerToUse = footerTesting;
  }

  // Transform the subnavMenu items if they exist
  const transformedSubnavMenu = subnavMenu ? {
    ...subnavMenu,
    items: transformMenuItems(subnavMenu.items),
  } : null;

  const shouldShowSubnav = transformedSubnavMenu?.items && transformedSubnavMenu.items.length > 0;

  useEffect(() => {
    optimizely?.track('client_pageview2');
  }, []);

  return (
    <div className={bodyClass}>
      {(path === '/') && <SiteNavigationElementSchema />}
      {showHeader && (
        <>
          <Header
            alertBar={alertBarData}
            homeLink={siteMetadata?.pathPrefix ? `${siteMetadata?.pathPrefix}/` : '/'}
            navigationBackgroundColor={navigationColor}
            navigationStyle={navigationStyle}
            path={path}
            scrollIndicator={scrollIndicator}
            siteMetadata={siteMetadata}
            utilityChildren={navigationStyle === 'power_up' ? <KlaviyoStatusIndicator /> : null}
            universalCtas={navigationStyle === 'default' ? (loggedIn ? ctasLoggedIn : ctas) : helpCenterCTAData}
            universalNavigationItems={['default', 'testing'].includes(navigationStyle) ? headerToUse : helpCenterHeaderData}
            utilityNavigationItems={utility}
            showSubnav={shouldShowSubnav}
            translations={translations}
            sites={sites}
          />
          {shouldShowSubnav && (
            <SubNav
              ctas={navigationStyle === 'default' ? (loggedIn ? ctasLoggedIn : ctas) : helpCenterCTAData}
              menuItems={(transformedSubnavMenu?.items || []) as ISubNavItem[]}
              path={path}
              universalCtas={navigationStyle === 'default' ? (loggedIn ? ctasLoggedIn : ctas) : helpCenterCTAData}
            />
          )}
        </>
      )}
      <main id="site-content" role="main">
        {shouldShowBreadcrumbs && !shouldShowSubnav && <Breadcrumbs background={breadcrumbsBackground} breadcrumbs={crumbs} colorVariant={breadcrumbColor} />}
        {children}
      </main>
      {showFooter && (
        <UniversalFooter
          backgroundColor='cotton'
          menuItems={footerToUse}
          copyrightMenuItems={copyright}
          navigationStyle={navigationStyle}
          siteMetadata={siteMetadata}
          sites={sites}
          translations={translations}
        />
      )}
    </div>
  );
};

export default withOptimizely(Layout);
